import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiChatBubbleLeftRight } from 'react-icons/hi2';
import { useQueryClient } from '@tanstack/react-query';
import { useMessagingUnreadCount } from '@/hooks/useMessagingHooks';
import { connectSocket, getSocket } from '@/lib/socket';
import type { ApiResponse, MessagingUnreadCountResponse } from '@amuster/types';

export function MessageIcon() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: unreadResp } = useMessagingUnreadCount();
  const totalUnread = unreadResp?.data?.total_unread ?? 0;

  // Real-time: increment unread on new message via WebSocket
  useEffect(() => {
    const socket = getSocket() ?? connectSocket();
    if (!socket) return;

    const onNewMessage = () => {
      // Optimistically increment unread count for instant badge update
      qc.setQueryData<ApiResponse<MessagingUnreadCountResponse>>(
        ['messaging', 'unread-count'],
        (old) => {
          const current = old?.data?.total_unread ?? 0;
          return {
            ...(old ?? { success: true, data: { total_unread: 0 } }),
            data: { total_unread: current + 1 },
          };
        },
      );

      // Also invalidate conversations list so new messages appear
      void qc.invalidateQueries({ queryKey: ['messaging', 'conversations'] });
    };

    socket.on('messaging:new', onNewMessage);
    return () => {
      socket.off('messaging:new', onNewMessage);
    };
  }, [qc]);

  return (
    <button
      type="button"
      onClick={() => navigate('/messages')}
      className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100 transition-colors"
      aria-label="Messages"
    >
      <HiChatBubbleLeftRight className="h-5 w-5" />
      {totalUnread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-blue-600 px-1 text-xs font-semibold leading-none text-white">
          {totalUnread > 99 ? '99+' : totalUnread}
        </span>
      )}
    </button>
  );
}
